import { ErrorBoundary, Stack, VStack } from '@genial/components';
import { Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box, Center, FlatList } from 'native-base';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import {
  TableHeadContainer,
  TableHeadItem,
  TableHeadItemOrderBy,
} from '../../../../../../components/TableHead';
import { useDynamicOrdinationContext } from '../../../../../../contexts/DynamicOrdination';
import {
  FiltersToApplyProps,
  useFilterTransformer,
} from '../../../../../../hooks/useFilterTransformer';
import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import {
  CustomerDetailStackParamList,
  SignedInNavigationScreenNavigationProp,
} from '../../../../../../routes/types';
import { securitiesTransferFiltersAtom } from '../../../../../../store/customerDetail/SecuritiesTransfer/filters';
import {
  investmentTransferRequestQuery,
  updateInvestmentsTransferPageSelector,
  updateInvestmentsTransferPeriodSelector,
  updateInvestmentsTransferSearchTermSelector,
} from '../../../../../../store/customerDetail/SecuritiesTransfer/selector';
import { SecuritiesTransferListItemNormalized } from '../../../../../../store/customerDetail/SecuritiesTransfer/types';

import { tableHead } from './constants';
import { SkeletonItem } from './SkeletonItem';
import { TransferRequestsItem } from './TransferRequestsItem';

interface TableTransferRequestsProps {}

export const ItemSeparator: React.FC = () => (
  <Box
    height={Utils.Responsive.PhoneTablet({
      phone: 'stack.xxxs',
      tablet: 'stack.nano',
    })}
  />
);

export const TableTransferRequests: React.FC<TableTransferRequestsProps> = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const searchTerm = useRecoilValue(updateInvestmentsTransferSearchTermSelector);
  const period = useRecoilValue(updateInvestmentsTransferPeriodSelector);
  const [page, setPage] = useRecoilState(updateInvestmentsTransferPageSelector);

  const [loadingComplete, setLoadingComplete] = useState<boolean>(false);
  const [allTransferRequests, setAllTransferRequests] = useState<
    SecuritiesTransferListItemNormalized[]
  >([]);
  const [filtersToApply, setFiltersToApply] = useState<FiltersToApplyProps>({});
  const [orderByApplied, setOrderByApplied] = useState<TableHeadItemOrderBy>();
  const [{ appliedFilters, availableFilters }] = useRecoilState(securitiesTransferFiltersAtom);

  const { transformFilters } = useFilterTransformer(availableFilters);
  const { appliedOrderBy, handleAutoOrderBy } = useDynamicOrdinationContext();

  useEffect(() => {
    setPage(1);
    setFiltersToApply(transformFilters(appliedFilters, { transformDateFilter: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    setPage(1);
    setOrderByApplied(appliedOrderBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedOrderBy]);

  const {
    data: response,
    error,
    loading,
    refresher,
  } = useRecoilQuery(
    investmentTransferRequestQuery({
      accountNumber: route.params.accountNumber,
      page,
      searchTerm,
      appliedOrderBy: orderByApplied,
      startDate: period?.startDate,
      endDate: period?.endDate,
      filtersToApply,
    })
  );

  const handleDetails = (protocolNumber: string) => {
    navigation.navigate('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'SecuritiesTransferDetail',
        params: {
          selectedTab: 'securitiesTransfer',
          ...route.params,
          protocolNumber,
        },
      },
    });
  };

  const handleEndReached = () => {
    if (!response?.meta.pagination.hasNext || !loadingComplete || loading || error) {
      return;
    }

    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    if (!response?.data) {
      return;
    }

    if (page === 1) {
      setAllTransferRequests(response.data);
    } else {
      setAllTransferRequests((prev) => [...prev, ...response.data]);
    }

    setLoadingComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.data]);

  useEffect(() => {
    if (loading) {
      setLoadingComplete(false);
    }
  }, [loading]);

  const hasActiveFilters =
    !!searchTerm ||
    !!period?.startDate ||
    !!period?.endDate ||
    Object.keys(filtersToApply).length > 0;

  if (error) {
    return <ErrorBoundary onRefresh={refresher} {...error} />;
  }

  if (!loading && allTransferRequests.length === 0) {
    return (
      <Center h="400px">
        {hasActiveFilters ? (
          <ErrorBoundary
            errorIconOrIllustration="NotFoundIllustration"
            errorTitle="Nenhum resultado corresponde à sua pesquisa"
            errorMessage="Por favor, verifique o campo informado e tente novamente."
          />
        ) : (
          <ErrorBoundary
            errorIconOrIllustration="NotEmptyIllustration"
            errorTitle="Nenhuma solicitação disponível no momento"
            errorMessage="Este cliente não possui nenhuma solicitação disponível."
          />
        )}
      </Center>
    );
  }

  return (
    <VStack space="stack.nano" pb="stack.nano">
      <Stack px="inline.xxxs">
        <TableHeadContainer>
          {tableHead.map((item, index) => (
            <TableHeadItem
              key={`${item.title}-${index}`}
              {...item}
              value={item.orderValue}
              orderBy={appliedOrderBy}
              onPress={() => handleAutoOrderBy(item.orderValue as string)}
            />
          ))}
        </TableHeadContainer>
      </Stack>

      {loading && page === 1 && <SkeletonItem />}
      <FlatList
        maxH="800px"
        data={allTransferRequests}
        onEndReached={handleEndReached}
        onEndReachedThreshold={page === 1 ? 0.15 : 0.5}
        windowSize={10}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={5}
        showsVerticalScrollIndicator={false}
        ItemSeparatorComponent={ItemSeparator}
        keyExtractor={(keyItem) => keyItem.protocolNormalized}
        renderItem={({ item }) => (
          <TransferRequestsItem onPressDetails={handleDetails} item={item} loading={loading} />
        )}
        ListFooterComponent={
          loading && page > 1 ? <SkeletonItem length={1} mt="stack.nano" /> : null
        }
      />
    </VStack>
  );
};
